// Demo-only mockup of a Microsoft Teams channel message: what the incident
// bot's post into #incident-response would look like when a major incident
// fans out to Teams. Purely decorative — no Teams Graph/webhook call, no
// real channel, just a pixel-styled replica for the outage-simulation demo.
// Reuses the Teams purple already sampled from a real Teams screenshot for
// TeamsPage.tsx (see --ms-purple in styles/teams-ms.css) so this mockup
// matches the one real Teams surface already in the app, rather than
// guessing a new shade.
import '../styles/teams-post-mockup.css';

export interface TeamsMockIncident {
  id: string | number;
  severity: 'critical' | string;
  current_status?: string | null;
  impact_statement?: string | null;
  service_name?: string | null;
  affected_site?: string | null;
  estimated_users_impacted?: number | null;
  workaround?: string | null;
  is_demo?: boolean;
  state?: string | null;
  declared_at?: string | null;
}

function IcoShield() {
  return (
    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="#fff" strokeWidth="1.3" aria-hidden="true">
      <path d="M8 1.6 13.2 3.4V7.4C13.2 10.7 11 12.9 8 14.4 5 12.9 2.8 10.7 2.8 7.4V3.4Z" strokeLinejoin="round" />
      <path d="M5.6 8 7.2 9.6 10.6 6.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IcoBell() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
      <path d="M8 1.7c-2 0-3.4 1.6-3.4 3.6v2.1c0 .5-.2 1-.6 1.4L3 9.8v.7h10v-.7l-1-1c-.4-.4-.6-.9-.6-1.4V5.3c0-2-1.4-3.6-3.4-3.6z" strokeLinejoin="round" />
      <path d="M6.3 12.3a1.7 1.7 0 0 0 3.4 0" strokeLinecap="round" />
    </svg>
  );
}

function formatTeamsTime(iso: string | null | undefined): string {
  const d = iso ? new Date(iso) : new Date();
  if (isNaN(d.getTime())) return 'now';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// FactSet row — mirrors Adaptive Cards' FactSet: bold left-column label,
// right-column value, omitted entirely when the value is missing (matches
// the incident shape's optional/nullable fields).
function Fact({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="teams-mock-fact">
      <div className="teams-mock-fact-label">{label}</div>
      <div className="teams-mock-fact-value">{value}</div>
    </div>
  );
}

/**
 * Modal-wrapped mockup of a Teams bot post into #incident-response for a
 * simulated major incident. `onClose` closes the overlay (click on scrim,
 * the "×" corner button, or the card's own "View Incident" pill — the pill
 * is decorative and has no destination of its own in this demo, so it just
 * reuses onClose rather than doing nothing).
 */
export default function TeamsPostMockup({ incident, onClose }: { incident: TeamsMockIncident; onClose: () => void }) {
  const cardTitle = incident.service_name || 'Major Incident';
  const severityLabel = incident.severity
    ? incident.severity.charAt(0).toUpperCase() + incident.severity.slice(1)
    : 'Critical';
  const statusLabel = incident.current_status || 'Investigating';
  const usersLabel =
    typeof incident.estimated_users_impacted === 'number'
      ? incident.estimated_users_impacted.toLocaleString()
      : undefined;

  return (
    <div className="teams-mock-overlay" onClick={onClose}>
      <div className="teams-mock-card" onClick={(e) => e.stopPropagation()}>
        <div className="teams-mock-channel-bar">
          <span className="teams-mock-channel-hash">#</span>
          <span className="teams-mock-channel-name">incident-response</span>
          <button className="teams-mock-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="teams-mock-body">
          <div className="teams-mock-msg">
            <div className="teams-mock-avatar">
              <IcoShield />
            </div>
            <div className="teams-mock-msg-col">
              <div className="teams-mock-msg-head">
                <span className="teams-mock-sender">ATLAS Incident Bot</span>
                <span className="teams-mock-apptag">APP</span>
                <span className="teams-mock-time">Just now</span>
              </div>

              <div className="teams-mock-adaptive-card">
                <div className="teams-mock-accent" />
                <div className="teams-mock-card-body">
                  <div className="teams-mock-card-title">{cardTitle}</div>

                  <div className="teams-mock-factset">
                    <Fact label="Severity" value={severityLabel} />
                    <Fact label="Status" value={statusLabel} />
                    <Fact label="Impact" value={incident.impact_statement} />
                    <Fact label="Affected Site" value={incident.affected_site} />
                    <Fact label="Users Impacted" value={usersLabel} />
                    <Fact label="Workaround" value={incident.workaround} />
                  </div>

                  <button type="button" className="teams-mock-view-btn" onClick={onClose}>
                    View Incident
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="teams-mock-thread-reply">
            <div className="teams-mock-reply-avatar">
              <IcoBell />
            </div>
            <div className="teams-mock-reply-col">
              <span className="teams-mock-reply-text">
                <strong>PagerDuty</strong> — J. Alvarez acknowledged this incident
              </span>
              <span className="teams-mock-reply-time">{formatTeamsTime(incident.declared_at)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
